// src/features/RecoveryOverlay.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { postToApp } from "../bridges/appBridge";
import { AppLifecycle as L } from "../utils/appLifecycle";

const DEFAULT_CD = 3;
const ALLOW_ON_PC = false;

export default function RecoveryOverlay() {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("recover"); // 'recover' | 'offline' | 'error'
    const [countdown, setCountdown] = useState(DEFAULT_CD);
    const timerRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    const hasBridge = typeof window !== "undefined" && !!window.ReactNativeWebView;
    const isIOSWeb = L.isIOS();
    const isAllowedEnv = hasBridge || isIOSWeb || ALLOW_ON_PC;

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // ✅ show를 useCallback으로 고정 + PC 차단 로직 사용
    const show = useCallback(
        (m = "recover", cd = DEFAULT_CD) => {
            if (!isAllowedEnv) return; // PC 등 비대상 환경에선 표시 안 함
            clearTimer();
            setMode(m);
            setCountdown(cd);
            setOpen(true);

            if (m === "recover") {
                timerRef.current = setInterval(() => {
                    setCountdown((prev) => {
                        if (prev <= 1) {
                            clearTimer();
                            try { window.location.reload(); } catch { }
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            }
        },
        [clearTimer, isAllowedEnv]
    );

    const hide = useCallback(() => {
        clearTimer();
        setOpen(false);
    }, [clearTimer]);

    const handleReloadNow = useCallback(() => {
        clearTimer();
        try { window.location.reload(); } catch { }
    }, [clearTimer]);

    const handleGoHome = useCallback(() => {
        clearTimer();
        navigate("/ads/main", { replace: true, state: { from: location.pathname } });
        setTimeout(() => setOpen(false), 400);
    }, [clearTimer, navigate, location.pathname]);

    const handleRetryOnline = useCallback(() => {
        if (navigator.onLine) {
            show("recover", 2);
        }
    }, [show]);

    const handleExitApp = useCallback(() => {
        if (!hasBridge) {
            alert("앱 환경에서만 종료할 수 있어요.");
            return;
        }
        try {
            postToApp({ type: "EXIT_APP" });
        } catch (e) {
            console.log("EXIT_APP bridge failed", e);
        }
    }, [hasBridge]);

    // ✅ 문제 상황일 때만 열기
    useEffect(() => {
        const onSoftRecover = (e) => {
            const reason = e?.detail?.reason || "unknown";
            const SHOWABLE = new Set([
                "offline",
                "white-screen",
                "stale-heartbeat",
                "error",
                "manual", // 수동 테스트
            ]);
            if (!SHOWABLE.has(reason)) return;

            if (reason === "offline") show("offline", 0);
            else show("recover", DEFAULT_CD);
        };

        window.addEventListener("APP_SOFT_RECOVER", onSoftRecover);
        return () => window.removeEventListener("APP_SOFT_RECOVER", onSoftRecover);
    }, [show]);

    // ✅ 온라인/오프라인 반응 (의존성에 show 포함)
    useEffect(() => {
        const onOffline = () => show("offline", 0);
        const onOnline = () => {
            if (open && mode === "offline") show("recover", 2);
        };
        window.addEventListener("offline", onOffline);
        window.addEventListener("online", onOnline);
        return () => {
            window.removeEventListener("offline", onOffline);
            window.removeEventListener("online", onOnline);
        };
    }, [open, mode, show]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-white shadow-xl">
                <div className="px-5 pt-5">
                    {mode === "recover" && (
                        <>
                            <h3 className="text-lg font-semibold">앱 복구 중입니다</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                잠시만 기다려 주세요. {countdown}초 후 자동으로 새로고침합니다.
                            </p>
                        </>
                    )}
                    {mode === "offline" && (
                        <>
                            <h3 className="text-lg font-semibold">네트워크 연결이 필요합니다</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                인터넷 연결을 확인한 뒤 다시 시도해 주세요.
                            </p>
                        </>
                    )}
                    {mode === "error" && (
                        <>
                            <h3 className="text-lg font-semibold">일시적인 오류가 발생했습니다</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                다시 시도하거나 메인으로 이동해 주세요.
                            </p>
                        </>
                    )}
                </div>

                <div className="mt-5 space-y-2 px-5 pb-5">
                    {mode === "recover" && (
                        <>
                            <button
                                onClick={handleReloadNow}
                                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 active:bg-blue-800"
                            >
                                지금 새로고침
                            </button>
                            <button
                                onClick={handleGoHome}
                                className="w-full rounded-xl bg-gray-100 px-4 py-3 text-gray-800 hover:bg-gray-200 active:bg-gray-300"
                            >
                                메인으로 이동
                            </button>
                            {hasBridge && (
                                <button
                                    onClick={handleExitApp}
                                    className="w-full rounded-xl bg-red-50 px-4 py-3 text-red-600 hover:bg-red-100 active:bg-red-200"
                                >
                                    앱 종료
                                </button>
                            )}
                        </>
                    )}

                    {mode === "offline" && (
                        <>
                            <button
                                onClick={handleRetryOnline}
                                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 active:bg-blue-800"
                            >
                                연결 확인 후 다시 시도
                            </button>
                            <button
                                onClick={handleGoHome}
                                className="w-full rounded-xl bg-gray-100 px-4 py-3 text-gray-800 hover:bg-gray-200 active:bg-gray-300"
                            >
                                메인으로 이동
                            </button>
                        </>
                    )}

                    {mode === "error" && (
                        <>
                            <button
                                onClick={handleReloadNow}
                                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 active:bg-blue-800"
                            >
                                다시 시도
                            </button>
                            <button
                                onClick={handleGoHome}
                                className="w-full rounded-xl bg-gray-100 px-4 py-3 text-gray-800 hover:bg-gray-200 active:bg-gray-300"
                            >
                                메인으로 이동
                            </button>
                            <button
                                onClick={hide}
                                className="w-full rounded-xl px-4 py-3 text-gray-600 hover:bg-gray-50"
                            >
                                닫기
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
